import {
  FePerson,
  useCoverageBetaStore,
  useIsPersonQaActive,
} from "@/store/useCoverageBetaStore";
import { useBoxReshape } from "@/hooks/useBoxReshape";
import { getDetectionBoxGeometry } from "@/utils/detection-box-geometry";
import { cn } from "@/utils/utils";
import { useMemo } from "react";
import { BoxCornerHandles } from "../../BoxCornerHandles";
import { OrientedBoxOverlay } from "../../OrientedBoxOverlay";
import "../../QueryEmptyAreaDetectionBox.css";

interface Props {
  person: FePerson;
  /** Index in the full persons array — used for the "P{idx}" label. */
  idx: number;
}

/**
 * An unblurred-person box on the query image. Human-drawn, so it supports
 * drag-to-move and corner-resize in draw mode (mirrors the empty-area box).
 */
export const QueryPersonBox = (props: Props) => {
  const { person, idx } = props;
  const points = person.points ?? [];

  const isDrawMode = useCoverageBetaStore(
    (s) => s.activeMode === "draw-box" || s.activeMode === "draw-person",
  );
  const updatePersonPoints = useCoverageBetaStore((s) => s.updatePersonPoints);
  const isQaMode = useCoverageBetaStore((s) => s.isQaMode);
  const setActiveQaItemKey = useCoverageBetaStore((s) => s.setActiveQaItemKey);
  const isActivePerson = useIsPersonQaActive(idx);

  const { boxRef, livePoints, grab } = useBoxReshape({
    points,
    enabled: isDrawMode,
    onCommit: (next) => updatePersonPoints(person.id, next),
  });
  // Person boxes are human-drawn (so axis-aligned) today, but hydrated results
  // could carry an oriented quad; it's drawn, moved and resized as one.
  const shownPoints = livePoints ?? points;
  const { style: boundingBoxStyle, corners } = useMemo(
    () => getDetectionBoxGeometry(shownPoints),
    [shownPoints],
  );

  return (
    <div
      ref={boxRef}
      data-person-index={idx}
      {...grab("move")}
      onClick={() => {
        if (!isQaMode) return;
        setActiveQaItemKey(`person:${idx}`);
      }}
      className={cn(
        "absolute",
        // In draw mode the human box sits above the draw overlay (z-50) to
        // capture move/resize pointer events. In QA mode it needs pointer
        // events too, to be clickable; otherwise (plain viewing) it's
        // decorative only.
        isDrawMode
          ? "z-[55] cursor-move"
          : isQaMode
            ? "cursor-pointer"
            : "pointer-events-none",
        isActivePerson && "qa-pulse",
      )}
      style={{ ...boundingBoxStyle }}
    >
      {/* A deleted box (QA mode only — see QueryImageRelated) gets the neutral
          gray treatment used for deleted areas, not the normal red. */}
      <OrientedBoxOverlay
        corners={corners}
        className={
          person.isDeleted
            ? cn(
                "stroke-gray-400",
                isActivePerson ? "fill-gray-400/40" : "fill-gray-400/20",
              )
            : "stroke-red-500 fill-red-500/30"
        }
        strokeWidth={2}
        dashed={person.isDeleted}
      />

      {isDrawMode && (
        <BoxCornerHandles
          points={shownPoints}
          grab={grab}
          className="border-red-500"
        />
      )}

      <div
        className="text-white font-bold absolute"
        style={{ left: "50%", top: "50%", transform: "translate(-50%, -50%)" }}
      >
        P{idx}
      </div>
    </div>
  );
};
